<template>
  <videon-metric-tile
    :heading="heading"
    :description="description"
    :icon="icon"
    :color="color"
    @click.native="showChart = true"
    style="cursor: pointer;"
  >
    <v-dialog
      v-model="showChart"
      max-width="90%"
    >
      <v-card
        flat
        width="100%"
      >
        <v-card-title>
          {{ heading }}
          <v-spacer />
          <v-btn color="secondary" @click="metricExplorer" x-small outlined class="mr-3"><v-icon small>assessment</v-icon> Modify Query</v-btn>
          <v-btn icon small @click="showChart = false" class="mr-0 pl-1"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <line-chart :chart-data="chartData" :chart-options="chartOptions()" />
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <v-progress-linear
      :color="color"
      indeterminate
      buffer-value="0"
      v-if="loading"
      class="mt-3 mb-2"
    ></v-progress-linear>
    
    <line-chart v-if="!loading && chartData.datasets.length > 0" :chart-data="chartData" :chart-options="chartOptionsTile()" class="metric-tile-chart mt-2"/>
  </videon-metric-tile>
</template>

<script>
  export default {
    name: 'VideonChartTile',
    
    props: {
      heading: {
        type: String
      },
      icon: {
        type: String
      },
      
      device: {
        type: Object,
        required: true
      },
      
      metrics: {
        type: Array,
        required: true
      },
      
      fill: {
        type: Boolean,
        default: false
      },
      unit: {
        type: String,
        default: ''
      },
      thresholdWarning: {
        type: Number
      },
      thresholdCritical: {
        type: Number
      },
    },
    computed: {
      loading() {
        if (this.device && this.device.fetchingMetrics == true) {
          return true
        }
        return false
      },
      
      color() {
        if (this.last && this.thresholdCritical && this.last > this.thresholdCritical) {
          return 'red'
        } else if (this.last && this.thresholdWarning && this.last > this.thresholdWarning) {
          return 'orange'
        }
        
        return 'blue'
      },
      
      description() {
        if (this.last) {
          if (this.unit == 'bytes') {
            return this.$helpers.byteFormat(this.last)
          } else {
            return this.last + this.unit
          }
        }
        return 'Unknown'
      },
      
      metricNames() {
        return this.metrics.map( metric => {
          var metricParts = metric.split('|')
          return metricParts[1]
        })
      },
      
      chartData() {
        if (this.device && this.device.deviceMetrics) {
          var filteredData = this.device.deviceMetrics.metricValues.filter( metric => this.metricNames.includes(metric.metric_name) )
          
          return {
            datasets: filteredData.map((data, index) => ({
              label: data.label,
              data: data.data,
              spanGaps: false,
              lineTension: 0.2,
              fill: this.fill,
              borderWidth: 1,
              borderColor: this.$helpers.getColorHex(this.color, index),
              backgroundColor: this.$helpers.getColorHex(this.color, index),
              pointBackgroundColor: this.$helpers.getColorHex(this.color, index),
            }))
          }
        }
        
        return { datasets: [] }
      },
      
      last() {
        if (this.device && this.device.deviceMetrics && this.metrics.length > 0) {
          // simply just return the last value of the first metric
          return this.device.deviceMetrics.last(this.metrics[0])
        }
        return false
      }
    },
    data() {
      return {
        showChart: false,
      }
    },
    
    methods: {
      chartOptionsTile() {
        var chartOptions = {
          legend: {
            display: false,
          },
          tooltips: {
            enabled: false
          },
          scales: {
            x: {
              type: 'time',
              ticks: {
                display: false,
                autoSkip: true,
                source: 'data'
              }
            },
            y: {
              ticks: {
                display: true,
                callback: (value) => {
                  if (this.unit == 'bytes') {
                    return this.$helpers.byteFormat(value, false)
                  } if(this.unit == '%') {
                    return Number.isInteger(value) ? '' + value : null
                  } else {
                    return value.toFixed(2)
                  }
                }
              }
            }
          },
          elements: {
            point: {
              radius: 0,
              hoverRadius: 1,
            },
          }
        }
        
        if (this.unit == '%') {
          chartOptions.scales.y.min = 0
          chartOptions.scales.y.max = 100
        }
        
        return chartOptions
      },
      
      chartOptions() {
        const chartOptions = {
          showLines: true,
          scales: {
            x: {
              type: 'time',
              time: {
                tooltipFormat: 'YYYY/MM/DD HH:mm',
                displayFormats: {
                  second: 'YYYY/MM/DD HH:mm',
                  minute: 'YYYY/MM/DD HH:mm',
                  hour: 'YYYY/MM/DD HH:mm',
                  day: 'YYYY/MM/DD HH:mm',
                }
              },
              ticks: {
                display: true,
                autoSkip: true,
                source: 'data'
              }
            },
            y: {
              ticks: {
                display: true,
                callback: (value) => {
                  if (this.unit == 'bytes') {
                    return this.$helpers.byteFormat(value)
                  } else {
                    value = Number.isInteger(value) ? value : value.toFixed(2)
                    return '' + value + this.unit
                  }
                }
              }
            }
          },
          plugins: {
            legend: {
              display: false,
              align: 'start',
              position: 'bottom',
              labels: {
                padding: 15,
                usePointStyle: this.pointStyleInLegend
              }
            },
            tooltip: {
              enabled: true,
              usePointStyle: this.pointStyleInTooltip,
              callbacks: {
                label: (item) => {
                  var itemValue
                  if (this.unit == 'bytes') {
                    itemValue = this.$helpers.byteFormat(item.parsed.y)
                  } else {
                    itemValue = '' + item.formattedValue + this.unit
                  }
                  return item.dataset.label + ': ' + itemValue
                }
              }
            }
          }
        }
        
        if (this.unit == '%') {
          chartOptions.scales.y.min = 0
          chartOptions.scales.y.max = 100
        }
        
        return chartOptions
      },
      
      metricExplorer() {
        this.$router.push({ path: '/metrics', query: {
          'device_guids': [this.device.device_guid],
          'metrics': this.metrics
        }})
      },
      
    }
  }
</script>
<style scoped lang="scss">
  .metric-tile-chart {
    width: 95% !important;
    height: 55px !important;
  }
</style>